import { useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import HeroSection from '../HeroSection/HeroSection';
import FlightDetailsPage from '../../pages/FlightDetailsPage/FlightDetailsPage';
import AddPassengersPage from '../../pages/AddPassengersPage/AddPassengersPage';
import PaymentPage from '../../pages/PaymentPage/PaymentPage';
import ReturnFlightDetails from '../ReturnFlightDetails/ReturnFlightDetails';
import MultiCityFlightDetails from '../MultiCityFlightDetails/MultiCityFlightDetails';
import { mergeBookingData, serializeBookingData } from '../../utils/bookingQueryParams';
import bookingService from '../../services/bookingService';

const FlightBookingFlow = () => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  // Merge booking data from URL, state, and session
  const bookingData = mergeBookingData(location);

  // Get flight ID from URL params or location state
  const flightId = params.id || location.state?.selectedFlight?.id || bookingData.flight_id;
  const returnFlightId = location.state?.returnFlightId || location.state?.selectedFlights?.[1]?.id || bookingData.return_flight_id;
  const selectedFlights = location.state?.selectedFlights || bookingData.selectedFlights || [];
  const passengers = bookingData.passengers || 1;
  const routeType = location.state?.routeType || location.state?.searchData?.tripType || bookingData.routeType || 'One Way';
  const fromUrl = location.state?.fromUrl;

  // Determine initial step - if coming from FlightDetailsPage (has pricing), go to passengers
  const initialStep = location.state?.pricing || bookingData.passengerData ? 'passengers' : 'details';
  const [currentStep, setCurrentStep] = useState(initialStep);

  const tabs = [
    { id: 'home', label: 'Home', number: '1' },
    { id: 'flights', label: 'Flights', number: '2' },
    { id: 'details', label: 'Details', number: '3' },
    { id: 'passengers', label: 'Passengers', number: '4' },
    { id: 'payment', label: 'Payment', number: '5' }
  ];

  const handleTabClick = (tabId) => {
    if (tabId === 'home') {
      // Get search parameters from sessionStorage or URL
      const searchParams = sessionStorage.getItem('flight-search-params');
      if (searchParams) {
        navigate(`/search-flight?${searchParams}`);
      } else {
        navigate('/search-flight');
      }
    } else if (tabId === 'flights') {
      const flightListUrl = sessionStorage.getItem('flight-list-Url') || '/one-way-flight';
      navigate(flightListUrl);
    } else {
      // Revoke seat lock if navigating away from payment step
      if (currentStep === 'payment' && (tabId === 'passengers' || tabId === 'details')) {
        if (bookingData.booking_token) {
          console.log('🔓 Revoking seat lock before switching tab...');
          bookingService.revokeLock(bookingData.booking_token).catch(err => {
            console.warn('⚠️ Failed to revoke lock:', err);
          });
        }
      }
      setCurrentStep(tabId);
    }
  };

  const handlePassengersNext = (data) => {
    console.log('📝 FlightBookingFlow: handlePassengersNext called with data:', data);

    // Update the URL with passenger data so PaymentPage can access it
    const updatedBookingData = {
      ...bookingData,
      passengerData: data.passengers,
      passengers: data.totalPassengers,
      routeType: data.routeType,
      pricing: data.pricing,
      saveForFuture: data.saveForFuture,
      farePerPax: data.pricing?.farePerPax,
      totalFare: data.pricing?.totalFare,
      blockFare: data.pricing?.blockFare,
      amount: data.pricing?.isSeatPricing ? data.pricing?.totalFare : data.pricing?.blockFare,
      // Preserve selectedFlights data for multi-leg bookings
      selectedFlights: bookingData.selectedFlights || selectedFlights,
      // Include the seat lock token
      booking_token: data.booking_token
    };

    console.log('📦 FlightBookingFlow: Updated booking data:', updatedBookingData);

    // Serialize booking data to query string
    const queryString = serializeBookingData(updatedBookingData);
    const newUrl = `/flight-booking-flow/${flightId}?${queryString}`;

    console.log('🔗 FlightBookingFlow: Navigating to:', newUrl);

    // Use React Router's navigate to update URL and location state
    navigate(newUrl, {
      replace: true,
      state: {
        ...updatedBookingData,
        fromPassengers: true
      }
    });

    console.log('🔄 FlightBookingFlow: Setting current step to payment');
    setCurrentStep('payment');
    console.log('✅ FlightBookingFlow: Current step updated to payment');
  };

  return (
    <div className="w-full">
      {/* Hero Section */}
      <HeroSection />

      {/* Tabs Navigation */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="flex flex-wrap justify-center mx-auto gap-2 w-full py-4 px-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className="no-underline focus:outline-none transition-all"
            >
              <div className="mr-4">
                <div
                  className={`w-32 h-10 border-b-2 pr-7 pl-0.5 flex items-center justify-center gap-2 cursor-pointer transition-all ${currentStep === tab.id
                    ? 'border-gray-400'
                    : 'border-gray-400 hover:border-gray-500'
                    }`}
                >
                  <span
                    className={`text-2xl font-semibold ${currentStep === tab.id ? 'text-yellow-400' : 'text-gray-600'
                      }`}
                  >
                    {tab.number}
                  </span>
                  <span
                    className={`text-xs font-medium ${currentStep === tab.id ? 'text-gray-600' : 'text-gray-600'
                      }`}
                  >
                    {tab.label}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Booking Flow Content */}
      <div className="w-full bg-gray-50">
        {console.log('🎬 FlightBookingFlow rendering, currentStep:', currentStep, 'flightId:', flightId, 'routeType:', routeType)}

        {/* Multi-City Flight Details */}
        {currentStep === 'details' && routeType === 'Multicity' && selectedFlights.length > 0 && (
          <MultiCityFlightDetails
            selectedFlights={selectedFlights}
            passengers={passengers}
            onNext={() => setCurrentStep('passengers')}
          />
        )}

        {/* Return Flight Details */}
        {currentStep === 'details' && (routeType === 'Return' || routeType === 'Returns') && flightId && (
          <ReturnFlightDetails
            outboundFlightId={flightId}
            returnFlightId={returnFlightId || flightId}
            passengers={passengers}
            onNext={() => setCurrentStep('passengers')}
          />
        )}

        {/* One-Way Flight Details */}
        {currentStep === 'details' && routeType !== 'Return' && routeType !== 'Returns' && routeType !== 'Multicity' && flightId && (
          <FlightDetailsPage
            id={flightId}
            passengers={passengers}
            routeType={routeType}
            fromUrl={fromUrl}
            hideBreadcrumb={true}
            onNext={() => setCurrentStep('passengers')}
          />
        )}

        {currentStep === 'details' && !flightId && selectedFlights.length === 0 && (
          <div className="px-4 md:px-8 py-8">
            <div className="max-w-6xl mx-auto">
              <p className="text-red-600 font-semibold">Error: No flight selected. Please go back and select a flight.</p>
            </div>
          </div>
        )}

        {currentStep === 'passengers' && (routeType === 'Multicity' ? selectedFlights.length > 0 : flightId) && (
          <AddPassengersPage
            id={flightId}
            selectedFlights={routeType === 'Multicity' ? selectedFlights : undefined}
            routeType={routeType}
            onNext={handlePassengersNext}
            onBack={() => setCurrentStep('details')}
          />
        )}

        {currentStep === 'passengers' && !flightId && (
          <div className="px-4 md:px-8 py-8">
            <div className="max-w-6xl mx-auto">
              <p className="text-red-600 font-semibold">Error: No flight selected. Please go back and select a flight.</p>
            </div>
          </div>
        )}

        {currentStep === 'payment' && flightId && (
          <PaymentPage id={flightId} hideBreadcrumb={true} />
        )}

        {currentStep === 'payment' && !flightId && (
          <div className="px-4 md:px-8 py-8">
            <div className="max-w-6xl mx-auto">
              <p className="text-red-600 font-semibold">Error: No flight selected. Please go back and select a flight.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlightBookingFlow;
